import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Heading5,
  Icon,
  IconVariant,
  Image,
  JustifyContent,
  Paragraph,
  Space,
  TextAlign,
} from '@gousto-internal/citrus-react'

import GoustoPng from 'routes/Home/PriceComparisonTable/assets/Gousto.png'
import GoustoWebp from 'routes/Home/PriceComparisonTable/assets/Gousto.webp'
import HelloFreshPng from 'routes/Home/PriceComparisonTable/assets/HelloFresh.png'
import HelloFreshWebp from 'routes/Home/PriceComparisonTable/assets/HelloFresh.webp'
import MindfulChefPng from 'routes/Home/PriceComparisonTable/assets/MindfulChef.png'
import MindfulChefWebp from 'routes/Home/PriceComparisonTable/assets/MindfulChef.webp'
import { TableItemType } from 'routes/Home/PriceComparisonTable/enums'
import { TableItemData } from 'routes/Home/PriceComparisonTable/types'

import {
  ComparisonTableItemContainer,
  BadgeTextContainer,
  ImageContainer,
} from '../Containers.styled'
import { TableItemPicture } from '../TableItemPicture/TableItemPicture'

interface ItemImageProps {
  type: TableItemType
}
export function ItemImage({ type }: ItemImageProps) {
  switch (type) {
    case TableItemType.Gousto:
      return (
        <TableItemPicture
          nextGenImage={GoustoWebp}
          fallbackImage={GoustoPng}
          altText="gousto logo"
        />
      )
    case TableItemType.HelloFresh:
      return (
        <TableItemPicture
          nextGenImage={HelloFreshWebp}
          fallbackImage={HelloFreshPng}
          altText="hello fresh logo"
        />
      )
    case TableItemType.MindfulChef:
      return (
        <TableItemPicture
          nextGenImage={MindfulChefWebp}
          fallbackImage={MindfulChefPng}
          altText="mindful chef logo"
        />
      )
    default:
      return <Image alt="price comparison table item logo" width="100%" height="auto" />
  }
}

interface ColorData {
  priceTextColor: Color
  badgeBackgroundColor: Color
  badgeTextColor: Color
}

const getColors = (type: TableItemType): ColorData =>
  type === TableItemType.Gousto
    ? {
        priceTextColor: Color.Success_800,
        badgeBackgroundColor: Color.Success_50,
        badgeTextColor: Color.Success_900,
      }
    : {
        priceTextColor: Color.Error_900,
        badgeBackgroundColor: Color.Error_50,
        badgeTextColor: Color.Error_900,
      }

interface Props {
  item: TableItemData
}

export function ComparisonTableItem({ item }: Props) {
  const { priceTextColor, badgeBackgroundColor, badgeTextColor } = getColors(item.type)

  return (
    <ComparisonTableItemContainer
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      justifyContent={JustifyContent.FlexEnd}
      alignItems={AlignItems.Center}
      width={['104px', '144px', '144px', '159px']}
    >
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.Center}
        alignItems={AlignItems.Center}
        width="100%"
        height="100%"
      >
        <ImageContainer maxWidth="96px" maxHeight="52px">
          <ItemImage type={item.type} />
        </ImageContainer>
      </Box>

      <Box paddingV={2} paddingBottom={0}>
        <Heading5 color={priceTextColor}>
          {item.currency}
          {item.price}
        </Heading5>
      </Box>

      <Paragraph color={priceTextColor} size={2} fontWeight={FontWeight.Normal}>
        {item.priceDescription}
      </Paragraph>
      {/* TODO: This should be removed in future and replaced with citrus badge */}
      {/* for citrus badge not production ready */}
      <Space size={4} direction="vertical" />
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.Center}
        alignItems={AlignItems.Center}
        bg={badgeBackgroundColor}
        width="100%"
        minHeight="40px"
        paddingV={[1, 1, 2, 3]}
        paddingH={[0, 2, 3, 4]}
        borderRadius={1.5}
      >
        {item.type === TableItemType.Gousto ? (
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Row}
            justifyContent={JustifyContent.Center}
            alignItems={AlignItems.Center}
            color={badgeTextColor}
          >
            <Icon name="success" size={3} variant={IconVariant.Inherit} />
            <Space size={0.75} direction="horizontal" />
            <BadgeTextContainer size={1} fontWeight={FontWeight.Bold}>
              {item.priceText()}
            </BadgeTextContainer>
          </Box>
        ) : (
          <BadgeTextContainer
            color={badgeTextColor}
            size={1}
            fontWeight={FontWeight.Normal}
            textAlign={TextAlign.Center}
          >
            {item.priceText(item.currency, item.priceDiff)}
          </BadgeTextContainer>
        )}
      </Box>
    </ComparisonTableItemContainer>
  )
}
